import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { Message, ToolUiMetadata } from '../types/events';
import { useDirection } from '../i18n/useDirection';
import { getToolUiIcon, getToolUiTitle } from './ToolUiPanel';

// Configure marked for inline chat use
marked.setOptions({ breaks: true, gfm: true });

export interface MessageBubbleProps {
  /** The message to display */
  message: Message;
  /** Background color for user messages */
  userMessageBgColor: string;
  /** Text color for user messages */
  userMessageTextColor?: string;
  /** Background color for bot messages */
  botMessageBgColor?: string;
  /** Text color for bot messages */
  botMessageTextColor?: string;
  /** Avatar URL shown next to bot messages */
  avatarUrl?: string;
  /** Whether to show the avatar (last message in a consecutive group) */
  showAvatar?: boolean;
  /** Whether this is the first message in a consecutive group from the same sender */
  isFirstInGroup?: boolean;
  /** Whether this is the last message in a consecutive group from the same sender */
  isLastInGroup?: boolean;
  /** Whether this message's tool UI has already been answered */
  toolUiAnswered?: boolean;
  /** Short confirmation summary to display once the tool UI is answered */
  toolUiSummary?: string;
  /** Whether the answered confirmation stays tappable so the answer can be changed. */
  toolUiCanReopen?: boolean;
  /** Open the bottom sheet for this message's tool UI */
  onOpenToolSheet?: () => void;
}

export function MessageBubble({
  message,
  userMessageBgColor,
  userMessageTextColor = '#ffffff',
  botMessageBgColor = '#f3f4f6',
  botMessageTextColor = '#111827',
  avatarUrl,
  showAvatar = true,
  isFirstInGroup = true,
  isLastInGroup = true,
  toolUiAnswered = false,
  toolUiSummary,
  toolUiCanReopen = false,
  onOpenToolSheet,
}: MessageBubbleProps) {
  const { t } = useTranslation('widget');
  const { isRTL } = useDirection();
  const isUser = message.senderType === 'user';
  const isSystem = message.senderType === 'system';
  const toolUi = message.metadata?.tool_ui as ToolUiMetadata | undefined;

  if (isSystem) {
    return (
      <div className="text-center text-gray-500 text-xs py-2">
        {message.content}
      </div>
    );
  }

  const isBot = !isUser && !isSystem;

  // Grouped corner radius: consecutive bot messages get tighter corners where they stack
  const getBotBorderRadius = () => {
    const r = '16px';   // normal radius
    const s = '4px';    // stacked radius
    if (isRTL) {
      if (isFirstInGroup && isLastInGroup) return `${r} ${r} ${s} ${r}`;
      if (isFirstInGroup) return `${r} ${r} ${s} ${s}`;
      if (isLastInGroup) return `${r} ${s} ${s} ${r}`;
      return `${s} ${s} ${s} ${r}`;
    }
    if (isFirstInGroup && isLastInGroup) return `${r} ${r} ${r} ${s}`;   // single message
    if (isFirstInGroup) return `${r} ${r} ${s} ${s}`;                     // first in group
    if (isLastInGroup) return `${s} ${r} ${r} ${s}`;                      // last in group
    return `${s} ${r} ${s} ${s}`;                                          // middle
  };

  const getUserBorderRadius = () => {
    const r = '16px';
    const s = '4px';
    if (isRTL) {
      if (isFirstInGroup && isLastInGroup) return `${r} ${s} ${r} ${r}`;
      if (isFirstInGroup) return `${r} ${s} ${s} ${r}`;
      if (isLastInGroup) return `${r} ${r} ${s} ${s}`;
      return `${s} ${s} ${s} ${r}`;
    }
    if (isFirstInGroup && isLastInGroup) return `${r} ${r} ${s} ${r}`;
    if (isFirstInGroup) return `${r} ${r} ${s} ${s}`;
    if (isLastInGroup) return `${s} ${r} ${s} ${r}`;
    return `${s} ${r} ${s} ${s}`;
  };

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
      style={{ alignItems: 'flex-end' }}
    >
      {/* Avatar column: always 24px wide for bot messages to keep alignment */}
      {isBot && (
        <div style={{ width: '24px', height: '24px', flexShrink: 0, marginInlineEnd: '8px' }}>
          {showAvatar && (
            avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                aria-hidden="true"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div
                aria-hidden="true"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#9ca3af">
                  <path d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z" />
                </svg>
              </div>
            )
          )}
        </div>
      )}
      <div
        className={`
          max-w-[80%] px-4 py-2 break-words
          ${message.isStreaming ? 'animate-pulse' : ''}
        `}
        style={{
          borderRadius: isUser ? getUserBorderRadius() : getBotBorderRadius(),
          backgroundColor: isUser ? userMessageBgColor : botMessageBgColor,
          color: isUser ? userMessageTextColor : botMessageTextColor,
        }}
      >
        {message.content ? (
          // `auto` reads the direction off the message itself. A mirrored layout must not mirror the
          // words: a Dutch sentence inheriting `rtl` had its trailing "?" resolved to the other end
          // of the line, so it rendered as "?kunnen we je vandaag mee helpen". Lists indent from the
          // text's own start side for the same reason.
          <div
            dir="auto"
            className="text-sm prose prose-sm prose-invert-user max-w-none [&>p]:m-0 [&>p+p]:mt-1 [&>ul]:m-0 [&>ol]:m-0 [&>ul]:ps-4 [&>ol]:ps-4 [&_a]:underline [&_a]:[color:var(--cp-link-color)]"
            // A link in the visitor's own bubble follows that bubble's text color; in a bot bubble
            // it takes the accent color, which is the only place it stands out against the surface.
            style={{ '--cp-link-color': isUser ? userMessageTextColor : userMessageBgColor } as CSSProperties}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(message.content) as string) }}
          />
        ) : null}

        {/* Interactive tool UIs open in a bottom sheet. The chat shows a tappable
            trigger card while pending, then a compact confirmation once answered. */}
        {toolUi && (
          toolUiAnswered ? (
            <ToolUiConfirmation
              summary={toolUiSummary || t('sheet.done')}
              hint={toolUiCanReopen ? t('sheet.changeHint') : undefined}
              onClick={toolUiCanReopen ? onOpenToolSheet : undefined}
            />
          ) : (
            <ToolUiTriggerCard
              icon={getToolUiIcon(toolUi.ui_type)}
              title={getToolUiTitle(t, toolUi.ui_type)}
              hint={t('sheet.reopenHint')}
              isRTL={isRTL}
              onClick={onOpenToolSheet}
            />
          )
        )}

        {message.isStreaming && (
          <span
            className="inline-block w-1 h-4 bg-current animate-blink"
            style={{ marginInlineStart: '4px' }}
          />
        )}
      </div>
    </div>
  );
}

/** Tappable card shown in the chat for a pending tool UI; opens the bottom sheet. */
function ToolUiTriggerCard({
  icon,
  title,
  hint,
  isRTL,
  onClick,
}: {
  icon: string;
  title: string;
  hint: string;
  isRTL: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mt-3 w-full flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 ${
        isRTL ? 'text-right' : 'text-left'
      } hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 transition-colors`}
    >
      <span className="text-lg leading-none" aria-hidden="true">{icon}</span>
      <span className="flex-1 min-w-0" dir="auto">
        <span className="block text-sm font-medium text-gray-900 truncate">{title}</span>
        <span className="block text-xs text-gray-500">{hint}</span>
      </span>
      <svg
        width="16"
        height="16"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="text-gray-400 shrink-0"
        aria-hidden="true"
        style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
      >
        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
      </svg>
    </button>
  );
}

/** Compact confirmation chip shown once a tool UI has been answered. */
function ToolUiConfirmation({
  summary,
  hint,
  onClick,
}: {
  summary: string;
  /** Present only when the answer can be given again; renders the pill as a button. */
  hint?: string;
  onClick?: () => void;
}) {
  const checkmark = (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="#16a34a" aria-hidden="true">
      <path fillRule="evenodd" d="M16.704 5.29a.75.75 0 0 1 .006 1.06l-7.5 7.6a.75.75 0 0 1-1.07 0l-3.85-3.9a.75.75 0 1 1 1.07-1.05l3.315 3.36 6.97-7.06a.75.75 0 0 1 1.06-.01Z" clipRule="evenodd" />
    </svg>
  );

  if (!onClick) {
    return (
      <div
        className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
      >
        {checkmark}
        <span dir="auto">{summary}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={hint ? `${summary} — ${hint}` : summary}
      className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 transition-colors"
    >
      {checkmark}
      <span dir="auto">{summary}</span>
      {/* An icon, not the words: the club name is the long part of this pill and the chat window
          is 384px wide, so spelling out "tap to change" pushed the name to an ellipsis. The
          wording lives on in the accessible name above. */}
      <PencilIcon />
    </button>
  );
}

/** Edit affordance on the club confirmation. */
function PencilIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 20 20"
      fill="#9ca3af"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M13.586 3.586a2 2 0 1 1 2.828 2.828l-.793.793-2.828-2.828.793-.793ZM11.379 5.793 3.5 13.672V16.5h2.828l7.879-7.879-2.828-2.828Z" />
    </svg>
  );
}

export default MessageBubble;
